import React, { useState, useEffect, useRef } from "react";
import { Auth } from "aws-amplify";
import { queryInvoicesForResellers } from "src/config/invoice-tracker-client";
import { reconcileRow } from "./reconcileInvoices";
import {
  Alert,
  Box,
  Button,
  DatePicker,
  FormField,
  Grid,
  Header,
  Multiselect,
  MultiselectProps,
  Pagination,
  SpaceBetween,
  Spinner,
  Table,
  Tabs,
} from "@amzn/awsui-components-console";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  FuseAccountingCentralDatastore,
  GetFixedFeeConfigCommand,
  GetPlanCommand,
  UseCaseName,
} from "@amzn/fuse-accounting-central-datastore-client";
import { AuthService } from "src/auth/auth-service";
import { FullPageTitle } from "src/components/header/title";
import {
  createAllRegionClients,
  listAllRegionConfigs,
} from "src/config/facd-all-regions";
import { isProd } from "src/util/is-prod";
import "src/index.scss";

import { ReportRow, CcTotalRow } from "./types";
import { getLastDayOfPreviousMonth, addMonths, getFirstOfMonth } from "./utils";
import { computeReportRow } from "./computeReportRow";
import {
  ST_COLUMNS,
  LT_COLUMNS,
  CC_SUBTOTAL_COLUMNS,
  STATUS_COLORS,
} from "./columnDefinitions";
import { exportStCsv, exportLtCsv } from "./csvExport";

const legendLabelStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "#555",
};
const legendBadgeStyle: React.CSSProperties = {
  padding: "2px 8px",
  borderRadius: "3px",
  fontSize: "12px",
};
const STATUS_SORT_ORDER: Record<string, number> = {
  confirmed: 0,
  "tracker-sourced": 1,
  "amount-mismatch": 2,
  missing: 3,
  unreconciled: 4,
};

export const AppUnbilledArAging: React.FC = () => {
  const [authenticating, setAuthenticating] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [ldap, setLdap] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [progress, setProgress] = useState({ fetched: 0, total: 0 });
  const [reportDate, setReportDate] = useState(getLastDayOfPreviousMonth());
  const [selectedResellers, setSelectedResellers] = useState<
    MultiselectProps.Option[]
  >([]);
  const [selectedCompanyCodes, setSelectedCompanyCodes] = useState<
    MultiselectProps.Option[]
  >([]);

  const authObj = useRef(new AuthService());

  useEffect(() => {
    const init = async () => {
      await authObj.current.authenticate();
      setAuthenticating(false);
      setIsLoggedIn(authObj.current.getIsLoggedIn());
      setLdap(authObj.current.ldap);
      const userLdap = authObj.current.ldap;
      if (
        authObj.current.getIsLoggedIn() &&
        (userLdap.includes("bart-read-only") || userLdap.includes("bart-admin"))
      ) {
        await generateReport(reportDate);
      }
    };
    init();
  }, []);

  const generateReport = async (date: string) => {
    setLoading(true);
    setError(null);
    setRows([]);
    try {
      const clients = await createAllRegionClients();

      const reclassCutoff = addMonths(getFirstOfMonth(date), 1);

      const regionConfigs = await listAllRegionConfigs(clients);
      const activeConfigs = regionConfigs.filter(
        (rc) =>
          rc.config.dealStatus === "ENABLED" &&
          rc.config.environment === "PRODUCTION",
      );
      setProgress({ fetched: 0, total: activeConfigs.length });

      const lookupArd = async (
        planIds: string[],
        client: FuseAccountingCentralDatastore,
      ): Promise<{ companyCode: string; funcCurrency: string }> => {
        for (const planId of planIds) {
          try {
            const planResp = await client.send(new GetPlanCommand({ planId }));
            const plan = (planResp as any).plan ?? planResp;
            const ardId = plan?.ardId;
            if (!ardId) continue;
            // ARD is embedded in the plan response — no separate GetArd call needed
            const ard = plan?.ard;
            if (!ard) continue;
            // Extract company code from FIXED_FEE_RECLASS_UNBILLED_AR use case (GL 13003/18203)
            let companyCode = "";
            for (const uc of ard?.usecases ?? []) {
              if (uc?.name === UseCaseName.FIXED_FEE_RECLASS_UNBILLED_AR) {
                for (const line of uc?.uslLines ?? []) {
                  if (line?.companyCode) {
                    companyCode = line.companyCode;
                    break;
                  }
                }
                break;
              }
            }
            // Fallback: first company code from any use case
            if (!companyCode) {
              for (const uc of ard?.usecases ?? []) {
                for (const line of uc?.uslLines ?? []) {
                  if (line?.companyCode) {
                    companyCode = line.companyCode;
                    break;
                  }
                }
                if (companyCode) break;
              }
            }
            const result = {
              companyCode,
              funcCurrency: ard?.functionalCurrency ?? "USD",
            };
            return result;
          } catch (err) {
            console.error(`ARD lookup failed for plan ${planId}:`, err);
          }
        }
        return { companyCode: "", funcCurrency: "USD" };
      };

      let fetched = 0;
      const CONCURRENCY = 5;
      const reportRows: ReportRow[] = [];

      // Process in batches of CONCURRENCY
      for (let i = 0; i < activeConfigs.length; i += CONCURRENCY) {
        const batch = activeConfigs.slice(i, i + CONCURRENCY);
        const results = await Promise.all(
          batch.map(async ({ config, region }) => {
            try {
              const client = clients[region];
              const fullConfig = await client.send(
                new GetFixedFeeConfigCommand({ fixedFeeId: config.fixedFeeId }),
              );
              if (fullConfig.fixedFeeConfig) {
                const row = computeReportRow(
                  fullConfig.fixedFeeConfig,
                  date,
                  reclassCutoff,
                );
                if (row) {
                  const planIds =
                    fullConfig.fixedFeeConfig.dealConfig?.fusePlanIds ?? [];
                  const ardInfo = await lookupArd(planIds, client);
                  row.companyCode = ardInfo.companyCode;
                  row.funcCurrency = ardInfo.funcCurrency;
                  row.fxRate =
                    row.currencyCode !== ardInfo.funcCurrency
                      ? row.fxRate
                      : 1.0;
                  row.region = region;
                  return row;
                }
              }
            } catch (err) {
              console.error(
                `Failed to fetch config ${config.fixedFeeId}:`,
                err,
              );
            }
            return null;
          }),
        );
        for (const r of results) {
          if (r) reportRows.push(r);
        }
        fetched += batch.length;
        setProgress({ fetched, total: activeConfigs.length });
      }

      reportRows.sort(
        (a, b) =>
          a.companyCode.localeCompare(b.companyCode) ||
          a.resellerId.localeCompare(b.resellerId),
      );

      // Reconcile against InvoiceTracker DDB
      let reconciledRows = reportRows;
      try {
        const resellerIds = Array.from(
          new Set(reportRows.map((r) => r.resellerId).filter(Boolean)),
        );
        // Build boundary map: per reseller, use the earliest firstInvoiceBreakdownPeriod
        const boundaries: Record<string, string> = {};
        for (const row of reportRows) {
          if (!row.resellerId || !row.firstInvoiceBreakdownPeriod) continue;
          const existing = boundaries[row.resellerId];
          if (!existing || row.firstInvoiceBreakdownPeriod < existing) {
            boundaries[row.resellerId] = row.firstInvoiceBreakdownPeriod;
          }
        }
        const trackerData = await queryInvoicesForResellers(
          resellerIds,
          boundaries,
        );
        reconciledRows = reportRows.map((row) => {
          const records = trackerData[row.resellerId];
          if (records == null) return row; // query failed or not queried
          return reconcileRow(row, records);
        });
      } catch (err) {
        console.error(
          "InvoiceTracker reconciliation failed, showing report without status:",
          err,
        );
      }

      // Sort by status: confirmed first, then yellow, orange, red
      reconciledRows.sort(
        (a, b) =>
          (STATUS_SORT_ORDER[a.invoiceStatus] ?? 9) -
            (STATUS_SORT_ORDER[b.invoiceStatus] ?? 9) ||
          a.companyCode.localeCompare(b.companyCode) ||
          a.resellerId.localeCompare(b.resellerId),
      );

      setRows(reconciledRows);
    } catch (err: any) {
      setError(err.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  // Reseller filter
  const resellerOptions: MultiselectProps.Option[] = Array.from(
    new Set(rows.map((r) => r.resellerId).filter(Boolean)),
  )
    .sort()
    .map((id) => ({ label: id, value: id }));

  // Company code filter
  const companyCodeOptions: MultiselectProps.Option[] = Array.from(
    new Set(rows.map((r) => r.companyCode).filter(Boolean)),
  )
    .sort()
    .map((cc) => ({ label: cc, value: cc }));

  const filteredRows = rows.filter((r) => {
    if (
      selectedResellers.length > 0 &&
      !selectedResellers.some((s) => s.value === r.resellerId)
    )
      return false;
    if (
      selectedCompanyCodes.length > 0 &&
      !selectedCompanyCodes.some((s) => s.value === r.companyCode)
    )
      return false;
    return true;
  });

  // Company code totals - one row per company code, sums in functional currency
  const ccTotals: CcTotalRow[] = (() => {
    const grouped: Record<string, CcTotalRow> = {};
    for (const r of filteredRows) {
      const key = `${r.companyCode || "(none)"}|${r.funcCurrency}`;
      if (!grouped[key]) {
        grouped[key] = {
          companyCode: r.companyCode || "(none)",
          configs: 0,
          fcCurrency: r.funcCurrency,
          fcStEnding: 0,
          fcLtEnding: 0,
        };
      }
      grouped[key].configs++;
      grouped[key].fcStEnding += r.stEnding * r.fxRate;
      grouped[key].fcLtEnding += r.ltEnding * r.fxRate;
    }
    return Object.values(grouped).sort((a, b) =>
      a.companyCode.localeCompare(b.companyCode),
    );
  })();

  const stCollection = useCollection(filteredRows, {
    pagination: { pageSize: 50 },
    sorting: {},
  });
  const ltCollection = useCollection(filteredRows, {
    pagination: { pageSize: 50 },
    sorting: {},
  });

  if (authenticating) return <Spinner size="large" />;

  return (
    <div>
      <FullPageTitle
        title={`Unbilled AR Report${!isProd() ? " (Beta)" : ""}`}
        description="Short-term (GL 13003) and Long-term (GL 18203) unbilled accounts receivable for fixed fee contracts."
      />
      <Box margin={{ top: "l" }} />
      {!(ldap.includes("bart-read-only") || ldap.includes("bart-admin")) ? (
        <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
          <Alert
            header="Only authorized users can access this page!"
            type="error"
          />
        </Grid>
      ) : (
        <Box padding={{ left: "l", right: "l" }}>
          <SpaceBetween direction="vertical" size="l">
            {/* Controls */}
            <SpaceBetween direction="horizontal" size="m" alignItems="end">
              <FormField label="Report Date (end of month)">
                <DatePicker
                  value={reportDate}
                  onChange={({ detail }) => setReportDate(detail.value)}
                  placeholder="YYYY-MM-DD"
                />
              </FormField>
              <FormField label="Reseller ID Filter">
                <Multiselect
                  selectedOptions={selectedResellers}
                  onChange={({ detail }) =>
                    setSelectedResellers([...detail.selectedOptions])
                  }
                  options={resellerOptions}
                  placeholder="All resellers"
                  filteringType="auto"
                  tokenLimit={3}
                />
              </FormField>
              <FormField label="Company Code Filter">
                <Multiselect
                  selectedOptions={selectedCompanyCodes}
                  onChange={({ detail }) =>
                    setSelectedCompanyCodes([...detail.selectedOptions])
                  }
                  options={companyCodeOptions}
                  placeholder="All company codes"
                  filteringType="auto"
                  tokenLimit={3}
                />
              </FormField>
              <Button
                variant="primary"
                onClick={() => generateReport(reportDate)}
                loading={loading}
              >
                Generate Report
              </Button>
            </SpaceBetween>

            {loading && (
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <Spinner size="large" />
                <Box>
                  Loading configs... {progress.fetched}/{progress.total}
                </Box>
              </SpaceBetween>
            )}

            {error && (
              <Alert header="Error" type="error">
                {error}
              </Alert>
            )}

            {!loading && rows.length > 0 && (
              <>
                <Box margin={{ bottom: "s" }}>
                  <SpaceBetween direction="horizontal" size="m">
                    <span style={legendLabelStyle}>
                      <strong>Contract ID Legend:</strong>
                    </span>
                    <span
                      style={{
                        ...legendBadgeStyle,
                        backgroundColor: STATUS_COLORS.confirmed,
                      }}
                    >
                      🟢 Confirmed
                    </span>
                    <span
                      style={{
                        ...legendBadgeStyle,
                        backgroundColor: STATUS_COLORS["tracker-sourced"],
                      }}
                    >
                      🟡 Invoice from Tracker (verify GL posting)
                    </span>
                    <span
                      style={{
                        ...legendBadgeStyle,
                        backgroundColor: STATUS_COLORS["amount-mismatch"],
                      }}
                    >
                      🟠 Amount mismatch (investigate)
                    </span>
                    <span
                      style={{
                        ...legendBadgeStyle,
                        backgroundColor: STATUS_COLORS.missing,
                      }}
                    >
                      🔴 Missing invoice confirmation
                    </span>
                    <span
                      style={{
                        ...legendBadgeStyle,
                        border: "1px solid #ccc",
                      }}
                    >
                      ⚪ Unreconciled
                    </span>
                  </SpaceBetween>
                </Box>
                <Tabs
                  tabs={[
                    {
                      id: "st",
                      label: `GL 13003 - Short Term (${filteredRows.length} configs)`,
                      content: (
                        <Table
                          {...stCollection.collectionProps}
                          variant="container"
                          stickyColumns={{ first: 1, last: 0 }}
                          header={
                            <Header
                              counter={`(${filteredRows.length})`}
                              actions={
                                <Button
                                  onClick={() =>
                                    exportStCsv(filteredRows, reportDate)
                                  }
                                  iconName="download"
                                >
                                  Export CSV
                                </Button>
                              }
                            >
                              GL 13003 - Short Term Unbilled AR
                            </Header>
                          }
                          columnDefinitions={ST_COLUMNS}
                          items={stCollection.items}
                          pagination={
                            <Pagination {...stCollection.paginationProps} />
                          }
                        />
                      ),
                    },
                    {
                      id: "lt",
                      label: `GL 18203 - Long Term (${filteredRows.length} configs)`,
                      content: (
                        <Table
                          {...ltCollection.collectionProps}
                          variant="container"
                          stickyColumns={{ first: 1, last: 0 }}
                          header={
                            <Header
                              counter={`(${filteredRows.length})`}
                              actions={
                                <Button
                                  onClick={() =>
                                    exportLtCsv(filteredRows, reportDate)
                                  }
                                  iconName="download"
                                >
                                  Export CSV
                                </Button>
                              }
                            >
                              GL 18203 - Long Term Unbilled AR
                            </Header>
                          }
                          columnDefinitions={LT_COLUMNS}
                          items={ltCollection.items}
                          pagination={
                            <Pagination {...ltCollection.paginationProps} />
                          }
                        />
                      ),
                    },
                  ]}
                />

                {/* Company Code Subtotals */}
                <Box>
                  <Header variant="h3">Totals by Company Code</Header>
                  <Table
                    variant="container"
                    columnDefinitions={CC_SUBTOTAL_COLUMNS}
                    items={ccTotals}
                  />
                </Box>
              </>
            )}

            {!loading && rows.length === 0 && !error && (
              <Alert header="No data" type="info">
                No active fixed fee configs found for report date {reportDate}.
              </Alert>
            )}
          </SpaceBetween>
        </Box>
      )}
    </div>
  );
};
